import { useContext } from 'react';
import { NextPage } from 'next';

import { Routes } from 'types/routes';
import { Section } from 'types/sections';

import CmsSection from 'models/CmsSection';

import { withVariables } from 'utils/string';

import LocaleContext from 'components/context/Locale';
import Breadcrumbs from 'components/layout/Breadcrumbs/component';
import Head from 'components/layout/Head';
import PageContainer from 'components/layout/PageContainer';
import SectionsWrapper from 'components/modules/public/cms/SectionsWrapper';
import SectionWrapper from 'components/modules/public/cms/SectionWrapper';
import { Variants } from 'components/modules/public/layout/Navigation/types';
import PageNotFound from 'components/modules/public/pages/NotFound';
import WrapperPublic from 'components/wrappers/Public';

import SectionBlogs from './sections/Blogs';
import SectionBundle from './sections/Bundle';
import SectionBundleComparison from './sections/BundleComparision';
import SectionBundleDescription from './sections/BundleDescription';
import SectionBundleFaq from './sections/BundleFaq';
import SectionBundleStages from './sections/BundleStages';
import StyledComponent from './styles';
import { Props } from './types';

const PublicPageBundle: NextPage<Props> = ({
    bundle,
    notFound,
    cmsPage,
    articles,
}) => {
    if (notFound) return <PageNotFound />;

    const localeContext = useContext(LocaleContext);
    const translations = localeContext?.translations?.product;
    const translationsBreadcrumbs = localeContext?.translations?.breadcrumbs;

    const breadcrumbs = [{
        to: Routes.PublicHome,
        label: translationsBreadcrumbs.home,
    }, {
        to: Routes.PublicSpecialOffers,
        label: translationsBreadcrumbs.specialOffers,
    }, {
        to: withVariables(Routes.PublicSpecialOffersBundle, {
            slug: bundle.locale.slug,
        }),
        label: bundle.locale.name,
        active: true,
    }];

    const sections: Section[] = [
        {
            slug: 'breadcrumbs',
            order: 1,
            visible: true,
            children: (slug: string, cmsSection: CmsSection) => (
                <SectionWrapper
                    cmsSection={cmsSection}
                    key={slug}
                >
                    {(locale) => (<Breadcrumbs breadcrumbs={breadcrumbs} />)}
                </SectionWrapper>
            ),
        },
        {
            slug: 'bundle',
            order: 2,
            visible: true,
            children: (slug: string, cmsSection: CmsSection) => (
                <SectionWrapper
                    cmsSection={cmsSection}
                    key={slug}
                >
                    {(locale) => (
                        <SectionBundle
                            bundle={bundle}
                            {...translations?.content?.product}
                            {...locale}
                        />
                    )}
                </SectionWrapper>
            ),
        },
        {
            slug: 'bundleDescription',
            order: 3,
            visible: Array.isArray(bundle.descriptions) && bundle.descriptions.length > 0,
            children: (slug: string, cmsSection: CmsSection) => (
                <SectionWrapper
                    cmsSection={cmsSection}
                    key={slug}
                >
                    {(locale, override) => (
                        <SectionBundleDescription override={() => override({
                            infos: bundle.descriptions,
                        })}
                        />
                    )}
                </SectionWrapper>
            ),
        },
        {
            slug: 'bundleStages',
            order: 4,
            visible: bundle.stages.length > 0,
            children: (slug: string, cmsSection: CmsSection) => (
                <SectionWrapper
                    cmsSection={cmsSection}
                    key={slug}
                >
                    {(locale, override) => (
                        <SectionBundleStages
                            override={() => override({
                                stages: bundle.stages,
                            })}
                        />
                    )}
                </SectionWrapper>
            ),
        },
        {
            slug: 'bundleComparison',
            order: 5,
            visible: Array.isArray(bundle?.comparisons) && bundle?.comparisons.length > 0,
            children: (slug: string, cmsSection: CmsSection) => (
                <SectionWrapper
                    cmsSection={cmsSection}
                    key={slug}
                >
                    {() => (
                        <SectionBundleComparison comparisonsData={bundle.comparisons} />
                    )}
                </SectionWrapper>
            ),
        },
        {
            slug: 'bundleFaq',
            order: 6,
            visible: Array.isArray(bundle.faqs) && bundle.faqs.length > 0,
            children: (slug: string, cmsSection: CmsSection) => (
                <SectionWrapper
                    cmsSection={cmsSection}
                    key={slug}
                >
                    {(locale) => (
                        <SectionBundleFaq
                            faqs={bundle.faqs}
                            translations={{
                                ...translations?.content?.faq,
                                ...locale,
                                headline: locale?.headline || translations?.content?.faq?.translations?.headline,
                            }}
                        />
                    )}
                </SectionWrapper>
            ),
        },
        {
            slug: 'blogs',
            order: 7,
            visible: Array.isArray(articles) && articles.length > 0,
            children: (slug: string, cmsSection: CmsSection) => (
                <SectionWrapper
                    cmsSection={cmsSection}
                    key={slug}
                >
                    {(locale) => (
                        <SectionBlogs
                            translations={{
                                ...translations?.content?.blogs,
                                ...locale,
                            }}
                            articles={articles}
                        />
                    )}
                </SectionWrapper>
            ),
        },
    ];

    return (
        <WrapperPublic
            forceVariant={Variants.Dark}
            isBannerVisible={cmsPage?.data?.isBannerVisible}
            bannerContent={cmsPage?.data?.bannerContent}
            bannerLink={cmsPage?.data?.bannerLink}
        >
            <StyledComponent className="public-page-special-offer-bundle">
                <Head
                    title={bundle?.locale?.metaTitle || translations?.meta?.title}
                    description={bundle?.locale?.metaDescription || translations?.meta?.description}
                    keywords={cmsPage?.data?.locale?.keywords || translations?.meta?.keywords}
                    tags={cmsPage?.data?.head}
                    cmsPageIndexingEnabled={cmsPage?.data?.indexingEnabled}
                />
                <PageContainer>
                    <SectionsWrapper
                        sections={sections}
                        cmsSections={cmsPage?.data?.sections}
                    />
                </PageContainer>
            </StyledComponent>
        </WrapperPublic>
    );
};

export default PublicPageBundle;
